import { Component, createRef } from 'react'
import { instanceOf, shape, string } from 'prop-types'
import cn from 'classnames'

import Slick from './slick/slick.js'

import GateContext from '../../../../utilities/gateContext.js'
import sendTrackingEvent from '../../../../utilities/sendTrackingEvent.js'
import getPostWidth from './helper-functions/getPostWidth.js'
import Banner from '../../theme-components/banner.js'
import DesktopCarouselControl from './desktop-carousel-control.js'
import isDark from '../../../../utilities/isDark.js'
import loadWebFont from '../../../../utilities/loadWebFont.js'
import MobileCarouselControl from './mobile-carousel-control.js'
import Settings from '../../../../../models/settings.js'

import { VisiblePostsContext } from '../../../../contexts/visiblePostsContext.js'

import '../../../../../styles/themes/carousel/carousel.scss'
import CarouselPost from './carousel-post.js'

export default class CarouselTheme extends Component {
  static propTypes = {
    containerElement: shape().isRequired,
    context: string,
    feed: shape().isRequired,
    feedKey: string.isRequired,
    settings: instanceOf(Settings).isRequired,
  }

  // eslint-disable-next-line react/sort-comp
  static contextType = VisiblePostsContext

  static defaultProps = {
    context: undefined,
  }

  constructor(props) {
    super(props)

    const width = props.settings.containerWidth || props.containerElement?.offsetWidth

    this.slickRef = createRef()
    this.wrapperRef = createRef()

    this.state = {
      containerWidth: width,
      wrapperWidth: 0,
      autoplaySpeed: 8,
      hasLeftCarousel: false,
      slickAutoplay: false,
      observer: new IntersectionObserver(
        (entries) => {
          const sortedPostKeys = new Map()
          entries.forEach((entry) => {
            const { target } = entry
            const { postKey } = target.dataset
            if (entry.isIntersecting) {
              sortedPostKeys.set(postKey, true)
            } else if (!sortedPostKeys.has(postKey)) {
              sortedPostKeys.set(postKey, false)
            }
          })
          const { addVisiblePostId, removeVisiblePostId } = this.context
          sortedPostKeys.forEach((isVisible, postKey) => {
            if (isVisible) {
              addVisiblePostId(postKey)
            } else {
              removeVisiblePostId(postKey)
            }
          })
        },
        {
          root: (this.slickRef.current?.innerSlider?.list || this.wrapperRef).current,
          threshold: 0.2,
        },
      ),
      currentSlide: undefined,
      navigationClickedOrPressed: false,
      message: '',
    }
  }

  componentDidMount() {
    loadWebFont(this.props.settings)
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.handleResize()

    if (this.props.settings.theme === 'carousel-auto') {
      const autoplaySpeed = this.props.settings.highlightTimeout
      this.setState({ slickAutoplay: true, autoplaySpeed })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.currentSlide !== this.state.currentSlide &&
      this.state.navigationClickedOrPressed
    ) {
      const dataIndex = Number(this.state.currentSlide.getAttribute('data-index'))
      this.announce(
        this.props.settings.locale.getTranslation('gateCarouselIndicatorAriaLabel')(
          dataIndex,
          this.props.feed.posts.length,
        ),
      )
      this.setState({ navigationClickedOrPressed: false })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  announce(text) {
    // Clear then re-set to force re-announcement
    this.setState({ message: '' })
    setTimeout(() => this.setState({ message: text }), 100)
  }

  get feedStyles() {
    const { settings } = this.props
    return {
      fontFamily: settings.fontFamily,
      fontSize: settings.feedFontSize,
      backgroundColor: settings.feedBackgroundColor,
    }
  }

  getSlickSettings(filteredPosts) {
    const { settings } = this.props
    const { locale } = settings
    const isMobileView = this.state.containerWidth <= 480
    const numberOfVisiblePosts = Math.floor(this.state.wrapperWidth / this.postWidth)
    let slidesToShow = isMobileView ? settings.mobileSlidesToShow : numberOfVisiblePosts

    if (settings.slidesToShowEnabled) {
      slidesToShow = isMobileView
        ? settings.mobileSlidesToShow
        : Math.min(settings.slidesToShow, numberOfVisiblePosts)
    }

    if (Number.isNaN(slidesToShow) || slidesToShow < 1) {
      slidesToShow = 1
    }

    const baseSettings = {
      autoplay: this.state.slickAutoplay,
      speed: 250,
      slidesToScroll: 1,
      slidesToShow,
      initialSlide: 0,
      infinite: filteredPosts.length > numberOfVisiblePosts,
      rtl: locale.isRTL,
      slide: '.bzfy-c-carousel-slide-wrap',
    }

    if (this.props.settings.theme === 'carousel-auto') {
      baseSettings.autoplaySpeed = this.state.autoplaySpeed * 1000
    }

    return baseSettings
  }

  get postWidth() {
    return getPostWidth(this.state.wrapperWidth, this.props.settings)
  }

  handleResize = () => {
    const { props, wrapperRef } = this
    const containerWidth = props.settings.containerWidth || props.containerElement?.offsetWidth
    const wrapper = wrapperRef?.current
    let wrapperWidth = 0
    if (wrapper) {
      const style = getComputedStyle(wrapper)
      wrapperWidth =
        wrapper.clientWidth -
          parseFloat(style.getPropertyValue('padding-left')) -
          parseFloat(style.getPropertyValue('padding-right')) || containerWidth
    }
    this.setState({
      containerWidth,
      wrapperWidth,
    })
  }

  trackUserInteraction = () => {
    const { allowCookies, enableWebAnalytics } = this.props.settings
    sendTrackingEvent({
      allowCookies,
      enableWebAnalytics,
      flowKey: this.props.feedKey,
      action: 'click',
      target: 'flow',
      eventName: 'embed_carousel_next_post_clicked',
      eventLabel: 'carousel',
      eventAction: 'clicked',
      interactive: true,
    })
  }

  slidePrev = () => {
    this.trackUserInteraction()
    this.slickRef.current.slickPrev()
    this.setState({ navigationClickedOrPressed: true })
  }

  slideNext = () => {
    this.trackUserInteraction()
    this.slickRef.current.slickNext()
    this.setState({ navigationClickedOrPressed: true })
  }

  slideTo = (slideNumber) => {
    this.trackUserInteraction()
    this.slickRef.current.slickGoTo(slideNumber)
    this.setState({ navigationClickedOrPressed: true })
  }

  toggleAutoPlay = (toggle) => {
    this.setState({ slickAutoplay: toggle })
  }

  toggleHasLeftCarousel = () => {
    this.setState((prevstate) => ({ hasLeftCarousel: !prevstate.hasLeftCarousel }))
  }

  leaveCarousel = () => {
    this.toggleHasLeftCarousel()
    if (this.props.settings.theme !== 'carousel-auto') {
      document.querySelector('.bzfy-c-carousel-control').focus()
    }
  }

  handleSetCurrentSlide = (currentSlide) => {
    this.setState({ currentSlide })
  }

  renderCarouselControls() {
    const { settings } = this.props
    const { feedBackgroundColor } = settings
    const backgroundIsDark = isDark(feedBackgroundColor)

    const { isRTL } = settings.locale
    const leftClickHandler = isRTL ? this.slideNext : this.slidePrev
    const rightClickHandler = isRTL ? this.slidePrev : this.slideNext

    if (this.state.containerWidth <= 480) {
      return (
        <MobileCarouselControl
          aria-controls="carousel"
          aria-label={settings.locale.getTranslation('carouselControlButtonsAriaLabel')}
          backgroundIsDark={backgroundIsDark}
          leftFunc={leftClickHandler}
          rightFunc={rightClickHandler}
        />
      )
    }
    return (
      <DesktopCarouselControl
        aria-controls="carousel"
        aria-label={settings.locale.getTranslation('carouselControlButtonsAriaLabel')}
        backgroundIsDark={backgroundIsDark}
        leftFunc={leftClickHandler}
        locale={settings.locale}
        rightFunc={rightClickHandler}
      />
    )
  }

  renderFeedContent() {
    const { feed, settings } = this.props
    const { gutterSize, hasDisplayLimit, locale, postsToShow } = settings
    const displayLimit = hasDisplayLimit ? postsToShow : 29

    const filteredPosts = feed.posts.filter((p) => p.media.length > 0).slice(0, displayLimit)
    if (filteredPosts.length === 0) {
      return null
    }

    const slickSettings = this.getSlickSettings(filteredPosts)
    const padding = gutterSize / 2
    const maxWidth = filteredPosts.length * this.postWidth + gutterSize + 160
    const ariaLive = slickSettings.autoplay ? 'polite' : 'off'
    const shouldRenderControls =
      !settings.isAutoTheme && filteredPosts.length > slickSettings.slidesToShow

    const openPost = (post, openGate) => {
      if (this.slickRef.current.innerSlider.clickable) {
        openGate(post)
      }
    }

    return (
      <GateContext.Consumer>
        {({ openGate }) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            aria-live={ariaLive}
            className="bzfy-c-carousel-slider-wrapper"
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                this.setState({ navigationClickedOrPressed: true })
              }
            }}
          >
            <div
              ref={this.wrapperRef}
              className="bzfy-c-carousel-slider-wrapper__inner"
              style={{ display: 'inline-block', width: '100%' }}
            >
              <Slick {...slickSettings} ref={this.slickRef}>
                {filteredPosts.map((post, index) => (
                  <CarouselPost
                    key={post.key}
                    feedKey={this.props.feedKey}
                    filteredPosts={filteredPosts}
                    hasLeftCarousel={this.state.hasLeftCarousel}
                    index={index}
                    leaveCarousel={this.leaveCarousel}
                    locale={locale}
                    maxWidth={maxWidth}
                    observer={this.state.observer}
                    openGate={openGate}
                    openPost={openPost}
                    padding={padding}
                    post={post}
                    postWidth={this.postWidth}
                    setCurrentSlide={this.handleSetCurrentSlide}
                    settings={settings}
                    slickAutoplay={this.state.slickAutoplay}
                    slideTo={this.slideTo}
                    toggleAutoPlay={this.toggleAutoPlay}
                    toggleHasLeftCarousel={this.toggleHasLeftCarousel}
                  />
                ))}
              </Slick>
              {shouldRenderControls && this.renderCarouselControls()}
            </div>
          </div>
        )}
      </GateContext.Consumer>
    )
  }

  render() {
    const hasPosts = this.props.feed?.posts?.length !== 0
    if (!hasPosts) {
      return null
    }

    const { settings } = this.props
    const { containerWidth } = this.state
    const fixedHeight = settings.containerFixedHeight

    return (
      <div className="bzfy-t-feed-carousel" data-testid={settings.theme} style={this.feedStyles}>
        <div
          className={cn('bzfy-feed', {
            'bzfy-feed--fixed': !!fixedHeight,
            'bzfy-feed--has-banner-top': settings.showTopBanner,
          })}
          style={{ height: fixedHeight || 'auto' }}
        >
          {settings.showTopBanner && hasPosts ? <Banner settings={settings} /> : null}
          <div className="bzfy-o-container" style={{ maxWidth: containerWidth }}>
            {this.renderFeedContent()}
          </div>
          {settings.showBottomBanner && hasPosts ? <Banner settings={settings} /> : null}
        </div>
        <div aria-live="assertive" className="bzfy-o-visually-hidden" role="status">
          {this.state.message}
        </div>
      </div>
    )
  }
}
